/**
 * Selection Action Bar V2
 *
 * Floating Action Bar am unteren Bildschirmrand.
 * Zeigt Bulk-Aktionen für ausgewählte Bilder.
 */

import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckSquare, XSquare, Download, Trash2, X, CheckCheck, CheckCircle, XCircle, BoxSelect } from 'lucide-react';
import { Button } from '@/components/v2/ui';
import { cn } from '@/lib/utils';

export interface SelectionActionBarProps {
  /** Anzahl der ausgewählten Bilder */
  selectedCount: number;
  /** Gesamtanzahl der Bilder */
  totalCount: number;
  /** Callback: Alle auswählen */
  onSelectAll: () => void;
  /** Callback: Auswahl aufheben */
  onDeselectAll: () => void;
  /** Callback: Ausgewählte downloaden */
  onDownload: () => void;
  /** Callback: Ausgewählte löschen */
  onDelete: () => void;
  /** Ist gerade ein Download aktiv? */
  isDownloading?: boolean;
  /** Ist gerade ein Löschen aktiv? */
  isDeleting?: boolean;
  /** Callback: Ausgewählte freigeben (nur im Approval-Modus) */
  onApprove?: () => void;
  /** Callback: Ausgewählte ablehnen (nur im Approval-Modus) */
  onReject?: () => void;
  /** Ist gerade eine Bulk-Freigabe aktiv? */
  isApprovingBulk?: boolean;
  /** Ist gerade eine Bulk-Ablehnung aktiv? */
  isRejectingBulk?: boolean;
  /** Callback: Ausgewählte zu einem Produkt hinzufügen (nur wenn gesetzt) */
  onAddToProduct?: () => void;
}

export function SelectionActionBar({
  selectedCount,
  totalCount,
  onSelectAll,
  onDeselectAll,
  onDownload,
  onDelete,
  isDownloading = false,
  isDeleting = false,
  onApprove,
  onReject,
  isApprovingBulk = false,
  isRejectingBulk = false,
  onAddToProduct,
}: SelectionActionBarProps) {
  const { t } = useTranslation();
  // Refs for focus management
  const previouslyFocusedElementRef = useRef<HTMLElement | null>(null);
  const firstButtonRef = useRef<HTMLButtonElement>(null);

  // Focus management: Move focus to action bar when it appears, restore on dismiss
  useEffect(() => {
    if (selectedCount > 0) {
      // Bar is appearing: store current focus and focus first button
      previouslyFocusedElementRef.current = document.activeElement as HTMLElement;

      // Focus first button after a short delay to allow animation
      const timeoutId = setTimeout(() => {
        firstButtonRef.current?.focus();
      }, 100);

      return () => clearTimeout(timeoutId);
    } else if (previouslyFocusedElementRef.current) {
      // Bar is disappearing: restore previous focus
      previouslyFocusedElementRef.current.focus();
      previouslyFocusedElementRef.current = null;
    }
  }, [selectedCount]);

  // Escape key handler to dismiss selection
  useEffect(() => {
    // Only listen when bar is visible
    if (selectedCount === 0) return;

    const handleEscape = (event: KeyboardEvent) => {
      // Don't dismiss if user is typing in an input/textarea
      const target = event.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return;

      if (event.key === 'Escape') {
        event.preventDefault();
        onDeselectAll();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [selectedCount, onDeselectAll]);

  // Nicht anzeigen wenn nichts ausgewählt
  if (selectedCount === 0) {
    return null;
  }

  const allSelected = selectedCount === totalCount && totalCount > 0;

  return (
    <div
      className={cn(
        'fixed bottom-6 left-1/2 -translate-x-1/2 z-50',
        'animate-in slide-in-from-bottom-4 fade-in duration-200'
      )}
    >
      <div
        className={cn(
          'flex items-center gap-4 px-5 py-3 rounded-full',
          'bg-neutral-900 dark:bg-neutral-800',
          'shadow-lg shadow-black/20',
          'border border-neutral-700'
        )}
      >
        {/* Ausgewählte Anzahl */}
        <div className="flex items-center gap-2 text-white">
          <CheckSquare className="h-5 w-5 text-orange-400" />
          <span className="font-semibold">{selectedCount}</span>
          <span className="text-neutral-400">
            {selectedCount === 1 ? t('common.image') : t('common.images')} {t('common.selected')}
          </span>
        </div>

        {/* Divider */}
        <div className="w-px h-6 bg-neutral-700" />

        {/* Aktionen */}
        <div className="flex items-center gap-2">
          {/* Alle markieren / Auswahl aufheben */}
          <Button
            ref={firstButtonRef}
            variant="ghost"
            size="sm"
            onClick={allSelected ? onDeselectAll : onSelectAll}
            title={allSelected ? t('common.cancel') : t('common.selectAll')}
            className="text-neutral-300 hover:text-white hover:bg-neutral-700"
          >
            {allSelected ? (
              <>
                <XSquare className="h-4 w-4 mr-1.5" />
                {t('common.cancel')}
              </>
            ) : (
              <>
                <CheckCheck className="h-4 w-4 mr-1.5" />
                {t('common.all')}
              </>
            )}
          </Button>

          {/* Approval Actions (only shown when callbacks provided) */}
          {onReject && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onReject}
              disabled={isRejectingBulk}
              title={t('gallery.bulkSelectReject')}
              className="text-neutral-300 hover:text-white hover:bg-neutral-700"
            >
              {isRejectingBulk ? (
                <div className="h-4 w-4 mr-1.5 animate-spin rounded-full border-2 border-neutral-300 border-t-transparent" />
              ) : (
                <XCircle className="h-4 w-4 mr-1.5" />
              )}
              {t('common.reject')}
            </Button>
          )}

          {onApprove && (
            <Button
              size="sm"
              onClick={onApprove}
              disabled={isApprovingBulk}
              title={t('gallery.bulkSelectApprove')}
              className="bg-green-600 hover:bg-green-700 active:bg-green-800 text-white"
            >
              {isApprovingBulk ? (
                <div className="h-4 w-4 mr-1.5 animate-spin rounded-full border-2 border-white border-t-transparent" />
              ) : (
                <CheckCircle className="h-4 w-4 mr-1.5" />
              )}
              {t('common.approve')}
            </Button>
          )}

          {/* Zu Produkt hinzufügen (nur wenn Callback gesetzt) */}
          {onAddToProduct && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onAddToProduct}
              title={t('addToProduct.buttonOpen')}
              className="text-neutral-300 hover:text-white hover:bg-neutral-700"
            >
              <BoxSelect className="h-4 w-4 mr-1.5" />
              {t('addToProduct.buttonOpen')}
            </Button>
          )}

          {/* Download */}
          <Button
            variant="default"
            size="sm"
            onClick={onDownload}
            disabled={isDownloading}
            title={t('gallery.bulkSelectDownload')}
            className="bg-orange-500 hover:bg-orange-600 text-white"
          >
            {isDownloading ? (
              <div className="h-4 w-4 mr-1.5 animate-spin rounded-full border-2 border-white border-t-transparent" />
            ) : (
              <Download className="h-4 w-4 mr-1.5" />
            )}
            {t('common.download')}
          </Button>

          {/* Löschen */}
          <Button
            variant="destructive"
            size="sm"
            onClick={onDelete}
            disabled={isDeleting}
            title={t('gallery.bulkSelectDelete')}
          >
            {isDeleting ? (
              <div className="h-4 w-4 mr-1.5 animate-spin rounded-full border-2 border-white border-t-transparent" />
            ) : (
              <Trash2 className="h-4 w-4 mr-1.5" />
            )}
            {t('common.delete')}
          </Button>
        </div>

        {/* Schließen Button */}
        <button
          onClick={onDeselectAll}
          title={t('gallery.closeSelection')}
          aria-label={t('gallery.closeSelection')}
          className="ml-2 p-1.5 rounded-full text-neutral-400 hover:text-white hover:bg-neutral-700 transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export default SelectionActionBar;
